/**
 * "Before you send" / "Before you post": a name box and an email box, in a popup,
 * for a signed-in account that has neither.
 *
 * A phone-only account (909 open on prod, 2026-10-25) often has no name and never
 * an email. Login's more-info step asks, but it can be dismissed, so the ask comes
 * back at the two moments a name and an email matter to the person: the first chat
 * message (the seller sees their name; replies reach them by email) and posting a
 * listing (buyers see the name; sale alerts and payouts go to email).
 *
 * It never blocks. "Not now" goes ahead with the message or the listing, and so does
 * "Continue" once something is saved. Each box only shows for what is missing when
 * the popup opens, and reuses the panels /sell and /account already show.
 */
import NameCapture from '../name-capture/NameCapture';
import { useNameCapture } from '../name-capture/useNameCapture';
import EmailCapture from '../email-capture/EmailCapture';
import { useEmailCapture } from '../email-capture/useEmailCapture';
import { useUser } from '../../contexts';
import shared from '../../data/js/shared';

export type ContactCaptureSurface = 'chat' | 'sell';

export interface ContactCapturePopupProps {
  surface: ContactCaptureSurface;
  askName: boolean;
  askEmail: boolean;
  /** Close the popup and carry on with what the person was doing. */
  onContinue: () => void;
}

export default function ContactCapturePopup({ surface, askName, askEmail, onContinue }: ContactCapturePopupProps) {
  const copy = shared.contactCapture;
  const { fetchUserProfile, setProfile } = useUser();
  const refresh = async () => {
    const refreshed = await fetchUserProfile(true);
    if (refreshed) setProfile(refreshed);
  };
  const nameCapture = useNameCapture({ onSaved: refresh });
  const emailCapture = useEmailCapture({ surface: `${surface}_popup`, onVerified: refresh });

  const savedSomething = nameCapture.isSaved || emailCapture.isVerified;

  return (
    <div className="confirmPopup contact-capture-popup" data-testid={`contact-capture-${surface}`}>
      <h3 className="heading">{surface === 'chat' ? copy.chatHeading : copy.sellHeading}</h3>
      <p className="explain">{surface === 'chat' ? copy.chatExplain : copy.sellExplain}</p>
      {askName && <NameCapture capture={nameCapture} heading={copy.nameHeading} subheading={copy.nameSubheading} />}
      {askEmail && <EmailCapture capture={emailCapture} heading={copy.emailHeading} subheading={copy.emailSubheading} />}
      <button
        type="button"
        className={savedSomething ? 'greenButton' : 'hollowButton'}
        data-testid="contact-capture-continue"
        onClick={onContinue}
      >
        {savedSomething ? copy.continueLabel : copy.notNowLabel}
      </button>
    </div>
  );
}
